import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Container,
    Heading,
    SimpleGrid,
    Text,
    Select,
    Button,
    Stack,
    HStack,
    VStack,
    useToast,
    Skeleton,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Progress,
    Input,
    Flex,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Card,
    CardHeader,
    CardBody,
    Icon,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useColorModeValue,
    Switch,
    FormControl,
    FormLabel,
} from '@chakra-ui/react';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip,
} from 'recharts';
import { 
    FaFilter, 
    FaUsers, 
    FaTrophy, 
    FaExclamationTriangle, 
    FaChartLine,
    FaDownload,
    FaRedo,
    FaClock,
    FaLeaf,
    FaSeedling,
    FaBug,
    FaChartBar,
    FaArrowUp,
    FaArrowDown
} from 'react-icons/fa';
import { format } from 'date-fns';

// Local imports
import { useHarvestersPerformance } from './useHarvestersPerformance';
import { 
    DashboardFilters, 
    DateRangePreset,
    SortableColumns,
} from './types';
import { 
    getStatusColor, 
    getPredefinedDateRange, 
    generatePerformanceDistributionData,
    getComparisonColor,
    exportToCSV
} from './utils';

const HarvestersPerformance: React.FC = () => {
    const toast = useToast();
    
    // State management
    const [filters, setFilters] = useState<DashboardFilters>({
        dateRange: 'this_week',
        startDate: '',
        endDate: '',
        estateId: '',
        divisiId: '',
        limit: 50
    });
    const [showBenchmark, setShowBenchmark] = useState(true);
    
    // Update dates when predefined range changes
    useEffect(() => {
        if (filters.dateRange !== 'custom') {
            const range = getPredefinedDateRange(filters.dateRange);
            setFilters(prev => ({
                ...prev,
                startDate: range.start,
                endDate: range.end
            }));
        }
    }, [filters.dateRange]);

    // Use the custom hook
    const {
        dashboardData,
        filterOptions,
        sortedHarvesters,
        availableDivisis,
        dashboardLoading,
        filterLoading,
        dashboardError,
        refetchDashboard,
        handleSort,
        sortBy,
        sortOrder,
    } = useHarvestersPerformance(filters);

    // Chart data for performance distribution
    const performanceDistributionData = useMemo(() => {
        if (!dashboardData?.summary) return [];
        return generatePerformanceDistributionData(dashboardData.summary);
    }, [dashboardData?.summary]);

    // Handle filter changes
    const handleFilterChange = (key: keyof DashboardFilters, value: any) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        
        // Reset divisi when estate changes
        if (key === 'estateId') {
            setFilters(prev => ({ ...prev, divisiId: '' }));
        }
    };

    // Handle date range change
    const handleDateRangeChange = (range: DateRangePreset) => {
        setFilters(prev => ({ ...prev, dateRange: range }));
    };

    // Handle export
    const handleExport = useCallback(() => {
        if (!sortedHarvesters || sortedHarvesters.length === 0) {
            toast({
                title: 'Tidak ada data',
                description: 'Tidak ada data untuk diekspor',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const exportData = sortedHarvesters.map(harvester => ({
            'Nama Pemanen': harvester.pemanen_name,
            'ID Pemanen': harvester.emp_pemanen_id,
            'Total Skor': harvester.total_score,
            'Status': harvester.status_label,
            'Rasio Kematangan (%)': harvester.rasio_kematangan,
            'Rasio Buah Mentah (%)': harvester.rasio_buah_mentah,
            'Rasio Buah Busuk (%)': harvester.rasio_buah_busuk,
            'Rasio Tangkai Panjang (%)': harvester.rasio_tangkai_panjang,
            'Rerata Brondolan': harvester.rerata_brondolan,
            'Produktivitas Harian': harvester.produktivitas_harian,
            'Total TBS': harvester.total_tbs,
            'Hari Kerja': harvester.working_days,
            'Total Inspeksi': harvester.total_inspections,
        }));

        exportToCSV(exportData, 'kinerja_pemanen');
        
        toast({
            title: 'Export berhasil',
            description: 'Data telah diekspor ke file CSV',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    }, [sortedHarvesters, toast]);

    // Color theme
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const cardBg = useColorModeValue('white', 'gray.800');

    return (
        <Box bg={bgColor} minH="100vh" py={8}>
            <Container maxW="8xl">
                {/* Header */}
                <VStack spacing={6} align="stretch">
                    <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                        <VStack align="start" spacing={1}>
                            <Heading size="lg" color="green.600">
                                <Icon as={FaUsers} mr={3} />
                                Dashboard Kinerja Pemanen
                            </Heading>
                            <Text color="gray.600">
                                Monitor dan analisis performa kualitas hasil panen
                            </Text>
                        </VStack>
                        
                        <HStack spacing={3}>
                            <Button
                                leftIcon={<FaRedo />}
                                onClick={refetchDashboard}
                                isLoading={dashboardLoading}
                                colorScheme="green"
                                variant="outline"
                                size="sm"
                            >
                                Refresh
                            </Button>
                            <Button
                                leftIcon={<FaDownload />}
                                onClick={handleExport}
                                colorScheme="blue"
                                variant="outline"
                                size="sm"
                                isDisabled={!sortedHarvesters || sortedHarvesters.length === 0}
                            >
                                Export
                            </Button>
                        </HStack>
                    </Flex>

                    {/* Filters */}
                    <Card bg={cardBg}>
                        <CardHeader>
                            <HStack>
                                <Icon as={FaFilter} color="blue.500" />
                                <Heading size="md">Filter & Periode</Heading>
                            </HStack>
                        </CardHeader>
                        <CardBody>
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                                {/* Date Range Selector */}
                                <FormControl>
                                    <FormLabel>Periode</FormLabel>
                                    <Select
                                        value={filters.dateRange}
                                        onChange={(e) => handleDateRangeChange(e.target.value as DateRangePreset)}
                                    >
                                        <option value="this_week">Minggu Ini</option>
                                        <option value="this_month">Bulan Ini</option>
                                        <option value="last_7_days">7 Hari Terakhir</option>
                                        <option value="last_30_days">30 Hari Terakhir</option>
                                        <option value="custom">Kustom</option>
                                    </Select>
                                </FormControl>

                                {/* Custom Date Inputs */}
                                {filters.dateRange === 'custom' && (
                                    <>
                                        <FormControl>
                                            <FormLabel>Tanggal Mulai</FormLabel>
                                            <Input
                                                type="date"
                                                value={filters.startDate}
                                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Tanggal Akhir</FormLabel>
                                            <Input
                                                type="date"
                                                value={filters.endDate}
                                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                            />
                                        </FormControl>
                                    </>
                                )}

                                {/* Estate Filter */}
                                <FormControl>
                                    <FormLabel>Estate</FormLabel>
                                    <Select
                                        value={filters.estateId}
                                        onChange={(e) => handleFilterChange('estateId', e.target.value === '' ? '' : parseInt(e.target.value))}
                                        isDisabled={filterLoading}
                                    >
                                        <option value="">Semua Estate</option>
                                        {filterOptions?.estates.map(estate => (
                                            <option key={estate.id} value={estate.id}>
                                                {estate.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Divisi Filter */}
                                <FormControl>
                                    <FormLabel>Divisi</FormLabel>
                                    <Select
                                        value={filters.divisiId}
                                        onChange={(e) => handleFilterChange('divisiId', e.target.value === '' ? '' : parseInt(e.target.value))}
                                        isDisabled={!filters.estateId || filterLoading}
                                    >
                                        <option value="">Semua Divisi</option>
                                        {availableDivisis.map(divisi => (
                                            <option key={divisi.id} value={divisi.id}>
                                                {divisi.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Limit */}
                                <FormControl>
                                    <FormLabel>Jumlah Data</FormLabel>
                                    <Select
                                        value={filters.limit}
                                        onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                                    >
                                        <option value={25}>25 Pemanen</option>
                                        <option value={50}>50 Pemanen</option>
                                        <option value={100}>100 Pemanen</option>
                                    </Select>
                                </FormControl>

                                {/* Show Benchmark Toggle */}
                                <FormControl display="flex" alignItems="center">
                                    <FormLabel mb="0">
                                        Tampilkan Benchmark
                                    </FormLabel>
                                    <Switch
                                        isChecked={showBenchmark}
                                        onChange={(e) => setShowBenchmark(e.target.checked)}
                                        colorScheme="green"
                                    />
                                </FormControl>
                            </SimpleGrid>

                            {dashboardData?.period && (
                                <Alert status="info" mt={4}>
                                    <AlertIcon />
                                    <Box>
                                        <AlertTitle>Periode Data:</AlertTitle>
                                        <AlertDescription>
                                            {format(new Date(dashboardData.period.start_date), 'dd MMM yyyy')} - {' '}
                                            {format(new Date(dashboardData.period.end_date), 'dd MMM yyyy')}
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}
                        </CardBody>
                    </Card>

                    {/* Error Display */}
                    {dashboardError && (
                        <Alert status="error">
                            <AlertIcon />
                            <AlertTitle>Error!</AlertTitle>
                            <AlertDescription>{dashboardError}</AlertDescription>
                        </Alert>
                    )}

                    {/* Loading State */}
                    {dashboardLoading && (
                        <Stack spacing={4}>
                            <Skeleton height="200px" />
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                                <Skeleton height="120px" />
                                <Skeleton height="120px" />
                                <Skeleton height="120px" />
                                <Skeleton height="120px" />
                            </SimpleGrid>
                        </Stack>
                    )}

                    {/* Dashboard Content */}
                    {dashboardData && !dashboardLoading && (
                        <>
                            {/* Summary Cards */}
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                                <Stat bg={cardBg} p={6} rounded="lg" shadow="sm">
                                    <StatLabel>Total Pemanen</StatLabel>
                                    <StatNumber fontSize="3xl" color="blue.600">
                                        <Icon as={FaUsers} mr={2} />
                                        {dashboardData.summary.total_harvesters}
                                    </StatNumber>
                                    <StatHelpText>Dalam periode ini</StatHelpText>
                                </Stat>

                                <Stat bg={cardBg} p={6} rounded="lg" shadow="sm">
                                    <StatLabel>Excellent</StatLabel>
                                    <StatNumber fontSize="3xl" color="green.600">
                                        <Icon as={FaTrophy} mr={2} />
                                        {dashboardData.summary.excellent_count}
                                    </StatNumber>
                                    <StatHelpText>
                                        {dashboardData.summary.total_harvesters > 0 
                                            ? `${((dashboardData.summary.excellent_count / dashboardData.summary.total_harvesters) * 100).toFixed(1)}%`
                                            : '0%'
                                        } dari total
                                    </StatHelpText>
                                </Stat>

                                <Stat bg={cardBg} p={6} rounded="lg" shadow="sm">
                                    <StatLabel>Perlu Training</StatLabel>
                                    <StatNumber fontSize="3xl" color="red.600">
                                        <Icon as={FaExclamationTriangle} mr={2} />
                                        {dashboardData.summary.poor_count}
                                    </StatNumber>
                                    <StatHelpText>
                                        Status Poor
                                    </StatHelpText>
                                </Stat>

                                <Stat bg={cardBg} p={6} rounded="lg" shadow="sm">
                                    <StatLabel>Rata-rata Skor</StatLabel>
                                    <StatNumber fontSize="3xl" color="purple.600">
                                        <Icon as={FaChartLine} mr={2} />
                                        {dashboardData.harvesters.length > 0
                                            ? (dashboardData.harvesters.reduce((sum, h) => sum + h.total_score, 0) / dashboardData.harvesters.length).toFixed(1)
                                            : '0'
                                        }
                                    </StatNumber>
                                    <StatHelpText>Dari 100 poin</StatHelpText>
                                </Stat>
                            </SimpleGrid>

                            {/* Charts Row */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                                {/* Performance Distribution */}
                                <Card bg={cardBg}>
                                    <CardHeader>
                                        <Heading size="md">Distribusi Performa</Heading>
                                    </CardHeader>
                                    <CardBody>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={performanceDistributionData}
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                    label={({ name, value, percent }) => 
                                                        `${name}: ${value} (${(percent! * 100).toFixed(0)}%)`
                                                    }
                                                >
                                                    {performanceDistributionData.map((entry, index) => (
                                                        <Cell key={`cell-${entry.name}-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </CardBody>
                                </Card>

                                {/* Benchmark Comparison */}
                                {showBenchmark && dashboardData.benchmark && (
                                    <Card bg={cardBg}>
                                        <CardHeader>
                                            <Heading size="md">Benchmark KPI</Heading>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack spacing={4} align="stretch">
                                                <HStack justify="space-between">
                                                    <HStack>
                                                        <Icon as={FaLeaf} color="green.500" />
                                                        <Text>Rasio Kematangan</Text>
                                                    </HStack>
                                                    <Badge colorScheme="green" fontSize="md" p={2}>
                                                        {dashboardData.benchmark.avg_rasio_kematangan.toFixed(1)}%
                                                    </Badge>
                                                </HStack>
                                                
                                                <HStack justify="space-between">
                                                    <HStack>
                                                        <Icon as={FaSeedling} color="red.500" />
                                                        <Text>Rasio Buah Mentah</Text>
                                                    </HStack>
                                                    <Badge colorScheme="red" fontSize="md" p={2}>
                                                        {dashboardData.benchmark.avg_rasio_buah_mentah.toFixed(1)}%
                                                    </Badge>
                                                </HStack>
                                                
                                                <HStack justify="space-between">
                                                    <HStack>
                                                        <Icon as={FaBug} color="orange.500" />
                                                        <Text>Rasio Buah Busuk</Text>
                                                    </HStack>
                                                    <Badge colorScheme="orange" fontSize="md" p={2}>
                                                        {dashboardData.benchmark.avg_rasio_buah_busuk.toFixed(1)}%
                                                    </Badge>
                                                </HStack>
                                                
                                                <HStack justify="space-between">
                                                    <HStack>
                                                        <Icon as={FaChartBar} color="blue.500" />
                                                        <Text>Rerata Brondolan</Text>
                                                    </HStack>
                                                    <Badge colorScheme="blue" fontSize="md" p={2}>
                                                        {dashboardData.benchmark.avg_rerata_brondolan.toFixed(1)}
                                                    </Badge>
                                                </HStack>
                                                
                                                <HStack justify="space-between">
                                                    <HStack>
                                                        <Icon as={FaClock} color="purple.500" />
                                                        <Text>Produktivitas Harian</Text>
                                                    </HStack>
                                                    <Badge colorScheme="purple" fontSize="md" p={2}>
                                                        {dashboardData.benchmark.avg_produktivitas_harian.toFixed(1)}
                                                    </Badge>
                                                </HStack>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}
                            </SimpleGrid>

                            {/* Main Data Table */}
                            <Card bg={cardBg}>
                                <CardHeader>
                                    <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                                        <Heading size="md">Daftar Performa Pemanen</Heading>
                                        <HStack spacing={4}>
                                            <Text fontSize="sm" color="gray.600">
                                                Menampilkan {sortedHarvesters.length} dari {dashboardData.summary.total_harvesters} pemanen
                                            </Text>
                                        </HStack>
                                    </Flex>
                                </CardHeader>
                                <CardBody>
                                    <Box overflowX="auto">
                                        <Table variant="simple" size="sm">
                                            <Thead>
                                                <Tr>
                                                    <Th 
                                                        cursor="pointer" 
                                                        onClick={() => handleSort('pemanen_name' as SortableColumns)}
                                                        _hover={{ bg: 'gray.50' }}
                                                    >
                                                        Nama Pemanen
                                                        {sortBy === 'pemanen_name' && (
                                                            <Icon as={sortOrder === 'desc' ? FaArrowDown : FaArrowUp} ml={1} />
                                                        )}
                                                    </Th>
                                                    <Th 
                                                        cursor="pointer" 
                                                        onClick={() => handleSort('total_score' as SortableColumns)}
                                                        _hover={{ bg: 'gray.50' }}
                                                        isNumeric
                                                    >
                                                        Total Skor
                                                        {sortBy === 'total_score' && (
                                                            <Icon as={sortOrder === 'desc' ? FaArrowDown : FaArrowUp} ml={1} />
                                                        )}
                                                    </Th>
                                                    <Th>Status</Th>
                                                    <Th 
                                                        cursor="pointer" 
                                                        onClick={() => handleSort('rasio_kematangan' as SortableColumns)}
                                                        _hover={{ bg: 'gray.50' }}
                                                        isNumeric
                                                    >
                                                        Rasio Kematangan
                                                        {sortBy === 'rasio_kematangan' && (
                                                            <Icon as={sortOrder === 'desc' ? FaArrowDown : FaArrowUp} ml={1} />
                                                        )}
                                                    </Th>
                                                    <Th 
                                                        cursor="pointer" 
                                                        onClick={() => handleSort('rasio_buah_mentah' as SortableColumns)}
                                                        _hover={{ bg: 'gray.50' }}
                                                        isNumeric
                                                    >
                                                        Buah Mentah
                                                        {sortBy === 'rasio_buah_mentah' && (
                                                            <Icon as={sortOrder === 'desc' ? FaArrowDown : FaArrowUp} ml={1} />
                                                        )}
                                                    </Th>
                                                    <Th 
                                                        cursor="pointer" 
                                                        onClick={() => handleSort('rerata_brondolan' as SortableColumns)}
                                                        _hover={{ bg: 'gray.50' }}
                                                        isNumeric
                                                    >
                                                        Rerata Brondolan
                                                        {sortBy === 'rerata_brondolan' && (
                                                            <Icon as={sortOrder === 'desc' ? FaArrowDown : FaArrowUp} ml={1} />
                                                        )}
                                                    </Th>
                                                    <Th 
                                                        cursor="pointer" 
                                                        onClick={() => handleSort('produktivitas_harian' as SortableColumns)}
                                                        _hover={{ bg: 'gray.50' }}
                                                        isNumeric
                                                    >
                                                        Produktivitas
                                                        {sortBy === 'produktivitas_harian' && (
                                                            <Icon as={sortOrder === 'desc' ? FaArrowDown : FaArrowUp} ml={1} />
                                                        )}
                                                    </Th>
                                                    <Th>Rekomendasi</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {sortedHarvesters.map((harvester, index) => (
                                                    <Tr key={harvester.pemanen_id} _hover={{ bg: 'gray.50' }}>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                <Text fontWeight="medium">
                                                                    {harvester.pemanen_name}
                                                                </Text>
                                                                <Text fontSize="xs" color="gray.500">
                                                                    ID: {harvester.emp_pemanen_id}
                                                                </Text>
                                                            </VStack>
                                                        </Td>
                                                        <Td isNumeric>
                                                            <VStack spacing={1}>
                                                                <Text fontWeight="bold" fontSize="lg">
                                                                    {harvester.total_score.toFixed(1)}
                                                                </Text>
                                                                <Progress 
                                                                    value={harvester.total_score} 
                                                                    size="sm" 
                                                                    colorScheme={getStatusColor(harvester.status)}
                                                                    w="60px"
                                                                />
                                                            </VStack>
                                                        </Td>
                                                        <Td>
                                                            <Badge 
                                                                colorScheme={getStatusColor(harvester.status)}
                                                                variant="solid"
                                                            >
                                                                {harvester.status_label}
                                                            </Badge>
                                                        </Td>
                                                        <Td isNumeric>
                                                            <Text 
                                                                color={getComparisonColor(
                                                                    harvester.rasio_kematangan, 
                                                                    dashboardData.benchmark?.avg_rasio_kematangan || 0, 
                                                                    true
                                                                )}
                                                                fontWeight="medium"
                                                            >
                                                                {harvester.rasio_kematangan.toFixed(1)}%
                                                            </Text>
                                                        </Td>
                                                        <Td isNumeric>
                                                            <Text 
                                                                color={getComparisonColor(
                                                                    harvester.rasio_buah_mentah, 
                                                                    dashboardData.benchmark?.avg_rasio_buah_mentah || 0, 
                                                                    false
                                                                )}
                                                                fontWeight="medium"
                                                            >
                                                                {harvester.rasio_buah_mentah.toFixed(1)}%
                                                            </Text>
                                                        </Td>
                                                        <Td isNumeric>
                                                            <Text 
                                                                color={getComparisonColor(
                                                                    harvester.rerata_brondolan, 
                                                                    dashboardData.benchmark?.avg_rerata_brondolan || 0, 
                                                                    true
                                                                )}
                                                                fontWeight="medium"
                                                            >
                                                                {harvester.rerata_brondolan.toFixed(1)}
                                                            </Text>
                                                        </Td>
                                                        <Td isNumeric>
                                                            <Text 
                                                                color={getComparisonColor(
                                                                    harvester.produktivitas_harian, 
                                                                    dashboardData.benchmark?.avg_produktivitas_harian || 0, 
                                                                    true
                                                                )}
                                                                fontWeight="medium"
                                                            >
                                                                {harvester.produktivitas_harian.toFixed(1)}
                                                            </Text>
                                                        </Td>
                                                        <Td>
                                                            <VStack align="start" spacing={1}>
                                                                {harvester.recommendations.slice(0, 2).map((rec) => (
                                                                    <Badge 
                                                                        key={`${harvester.pemanen_id}-rec-${rec}`} 
                                                                        variant="outline" 
                                                                        colorScheme="blue"
                                                                        fontSize="xs"
                                                                    >
                                                                        {rec}
                                                                    </Badge>
                                                                ))}
                                                            </VStack>
                                                        </Td>
                                                    </Tr>
                                                ))}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                </CardBody>
                            </Card>

                            {/* Actionable Insights */}
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                                {/* Top Performers */}
                                {dashboardData.actionable_insights?.top_performers && dashboardData.actionable_insights.top_performers.length > 0 && (
                                    <Card bg={cardBg}>
                                        <CardHeader>
                                            <HStack>
                                                <Icon as={FaTrophy} color="gold" />
                                                <Heading size="md" color="green.600">
                                                    Top Performers
                                                </Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                {dashboardData.actionable_insights.top_performers
                                                    .sort((a, b) => b.total_score - a.total_score) // Sort by score descending
                                                    .slice(0, 5)
                                                    .map((performer, index) => (
                                                    <HStack key={performer.pemanen_id} p={3} bg="green.50" rounded="md">
                                                        <Badge colorScheme="green" variant="solid">
                                                            #{index + 1}
                                                        </Badge>
                                                        <VStack align="start" flex={1} spacing={0}>
                                                            <Text fontWeight="bold">
                                                                {performer.pemanen_name}
                                                            </Text>
                                                            <Text fontSize="sm" color="gray.600">
                                                                Skor: {performer.total_score.toFixed(1)} | 
                                                                Kematangan: {performer.rasio_kematangan.toFixed(1)}%
                                                            </Text>
                                                        </VStack>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Need Training */}
                                {dashboardData.actionable_insights?.need_training && dashboardData.actionable_insights.need_training.length > 0 && (
                                    <Card bg={cardBg}>
                                        <CardHeader>
                                            <HStack>
                                                <Icon as={FaExclamationTriangle} color="red.500" />
                                                <Heading size="md" color="red.600">
                                                    Perlu Pelatihan
                                                </Heading>
                                            </HStack>
                                        </CardHeader>
                                        <CardBody>
                                            <VStack align="stretch" spacing={3}>
                                                {dashboardData.actionable_insights.need_training
                                                    .sort((a, b) => a.total_score - b.total_score) // Sort by score ascending (worst first)
                                                    .slice(0, 5)
                                                    .map((performer) => (
                                                    <HStack key={performer.pemanen_id} p={3} bg="red.50" rounded="md">
                                                        <Icon as={FaExclamationTriangle} color="red.500" />
                                                        <VStack align="start" flex={1} spacing={0}>
                                                            <Text fontWeight="bold">
                                                                {performer.pemanen_name}
                                                            </Text>
                                                            <Text fontSize="sm" color="gray.600">
                                                                Skor: {performer.total_score.toFixed(1)} | 
                                                                Buah Mentah: {performer.rasio_buah_mentah.toFixed(1)}%
                                                            </Text>
                                                            <HStack spacing={1} mt={1}>
                                                                {performer.recommendations.slice(0, 1).map((rec) => (
                                                                    <Badge key={`${performer.pemanen_id}-training-rec-${rec}`} variant="outline" colorScheme="red" fontSize="xs">
                                                                        {rec}
                                                                    </Badge>
                                                                ))}
                                                            </HStack>
                                                        </VStack>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                )}
                            </SimpleGrid>
                        </>
                    )}
                </VStack>
            </Container>
        </Box>
    );
};

export default HarvestersPerformance;